import React, { useState, useEffect } from "react";
import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import { useAtom } from "../app-jotai";
import { authDialogStateAtom, authSessionAtom, authStorage } from "./auth-state";
import { authApi } from "./auth-api";
import "./AuthDialog.scss";

export const AuthDialog: React.FC = () => {
  const [dialogState, setDialogState] = useAtom(authDialogStateAtom);
  const [, setSession] = useAtom(authSessionAtom);

  const [activeTab, setActiveTab] = useState<"login" | "register">("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (dialogState.isOpen) {
      setActiveTab(dialogState.initialTab);
      setErrorMessage(null);
    }
  }, [dialogState.isOpen, dialogState.initialTab]);

  if (!dialogState.isOpen) {
    return null;
  }

  const handleClose = () => {
    setDialogState({ isOpen: false, initialTab: "login" });
    setErrorMessage(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage(null);

    try {
      if (activeTab === "login") {
        const res = await authApi.login({
          email: email.trim(),
          password,
        });
        authStorage.save(res.accessToken, res.user, res.refreshToken);
        setSession({
          user: res.user,
          accessToken: res.accessToken,
          isLoading: false,
          error: null,
        });
      } else {
        if (!name.trim()) {
          throw new Error("Please enter your name");
        }
        if (password.length < 8) {
          throw new Error("Password must be at least 8 characters long");
        }
        const res = await authApi.register({
          name: name.trim(),
          email: email.trim(),
          password,
        });
        authStorage.save(res.accessToken, res.user, res.refreshToken);
        setSession({
          user: res.user,
          accessToken: res.accessToken,
          isLoading: false,
          error: null,
        });
      }
      handleClose();
    } catch (err: any) {
      setErrorMessage(err.message || "Authentication failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      size="small"
      autofocus={false}
      onCloseRequest={handleClose}
      title={
        <div className="AuthDialog__header-wrapper">
          <div className="AuthDialog__header">
            <h2>{activeTab === "login" ? "Welcome Back" : "Create Account"}</h2>
            <p>
              {activeTab === "login"
                ? "Sign in to access your cloud canvases & workspaces"
                : "Sign up to collaborate and save your canvases in the cloud"}
            </p>
          </div>
          <button
            type="button"
            className="AuthDialog__close-btn"
            onClick={handleClose}
            title="Close (Esc)"
            aria-label="Close"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      }
    >
      <div
        className="AuthDialog"
        onKeyDown={(e) => e.stopPropagation()}
        onKeyUp={(e) => e.stopPropagation()}
      >
        <div className="AuthDialog__tabs">
          <button
            type="button"
            className={`AuthDialog__tab ${activeTab === "login" ? "is-active" : ""}`}
            onClick={() => {
              setActiveTab("login");
              setErrorMessage(null);
            }}
          >
            Sign In
          </button>
          <button
            type="button"
            className={`AuthDialog__tab ${activeTab === "register" ? "is-active" : ""}`}
            onClick={() => {
              setActiveTab("register");
              setErrorMessage(null);
            }}
          >
            Create Account
          </button>
        </div>

        {errorMessage && (
          <div className="AuthDialog__error">
            <span>{errorMessage}</span>
          </div>
        )}


        <form className="AuthDialog__form" onSubmit={handleSubmit}>
          {activeTab === "register" && (
            <div className="AuthDialog__field">
              <label htmlFor="auth-name">Full Name</label>
              <div className="AuthDialog__input-wrapper">
                <input
                  id="auth-name"
                  type="text"
                  placeholder="e.g. Alex Morgan"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
            </div>
          )}

          <div className="AuthDialog__field">
            <label htmlFor="auth-email">Email Address</label>
            <div className="AuthDialog__input-wrapper">
              <input
                id="auth-email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="AuthDialog__field">
            <label htmlFor="auth-password">Password</label>
            <div className="AuthDialog__input-wrapper">
              <input
                id="auth-password"
                type={showPassword ? "text" : "password"}
                placeholder={
                  activeTab === "register"
                    ? "At least 8 characters"
                    : "Enter your password"
                }
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="AuthDialog__toggle-pw"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="AuthDialog__submit"
            disabled={isLoading}
          >
            {isLoading ? (
              <span>Connecting...</span>
            ) : activeTab === "login" ? (
              <span>Sign In to Excalico</span>
            ) : (
              <span>Create Free Account</span>
            )}
          </button>
        </form>

        <div className="AuthDialog__footer">
          {activeTab === "login" ? (
            <span>
              Don't have an account?{" "}
              <button
                type="button"
                onClick={() => {
                  setActiveTab("register");
                  setErrorMessage(null);
                }}
              >
                Sign up
              </button>
            </span>
          ) : (
            <span>
              Already have an account?{" "}
              <button
                type="button"
                onClick={() => {
                  setActiveTab("login");
                  setErrorMessage(null);
                }}
              >
                Sign in
              </button>
            </span>
          )}

          <div style={{ marginTop: "0.5rem" }}>
            <button
              type="button"
              className="AuthDialog__cancel-btn"
              onClick={handleClose}
            >
              ← Cancel and return to canvas
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
